import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox,
  Pagination,
  Tabs,
  Tab,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getStoredToken, getStoredUser, getKivoProfiles, getKivoBranches } from '../utils/api';
import { buildApiUrl } from '../config/api';
import PageHeader from './PageHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Sync from '@mui/icons-material/Sync';

const companyCardColors = ['#6070C7', '#5A89CA', '#4E73D0'];

const ConnectWithKivo = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [fetchingConfig, setFetchingConfig] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [configList, setConfigList] = useState([]);
  // Pagination state for Kivo config API
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [attendanceToken, setAttendanceToken] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyBaseUrl, setCompanyBaseUrl] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [kivoProfilesByConfig, setKivoProfilesByConfig] = useState({});
  const [profilesLoadingMap, setProfilesLoadingMap] = useState({});
  const [profilesErrorMap, setProfilesErrorMap] = useState({});
  const [selectedProfileKeysByConfig, setSelectedProfileKeysByConfig] = useState({});
  const [selectedCompaniesByConfig, setSelectedCompaniesByConfig] = useState({});
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const [currentCompanyName, setCurrentCompanyName] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState('');
  const [refreshingCompanies, setRefreshingCompanies] = useState({});
  const defaultConfig = configList.find((cfg) => cfg.is_default) || configList[0] || null;
  const addedCompanies = (() => {
    const seen = new Set();
    const result = [];
    for (const cfg of configList) {
      const name = cfg.company_name || 'Default Company';
      const id = cfg.company_id || '';
      const key = `${name}:${id}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ companyName: name, companyId: id });
      }
    }
    return result;
  })();

  const showToast = (message, severity = 'success') => {
    if (severity === 'error') {
      setErrorMessage(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
      return;
    }
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 4000);
  };

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }
    (async () => {
      setPage(1);
      setTotalPages(1);
      await fetchKivoConfig({ targetPage: 1 });
    })();
  }, [navigate]);

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        const companyId = parsed.company_id || parsed.companyId || null;
        const companyName = parsed.company_name || parsed.companyName || parsed.company || '';
        setCurrentCompanyId(companyId ? String(companyId) : null);
        setCurrentCompanyName(companyName);
        setCurrentUsername(parsed.username || parsed.email || parsed.name || 'Current User');
      } catch (err) {
        console.error('Failed to parse stored user for company info', err);
      }
    }
  }, []);
  const groupedConfigs = useMemo(() => {
    const groups = {};
    (configList || []).forEach((cfg) => {
      const uname = cfg.username || cfg.user_name || cfg.email || 'Other Users';
      if (!groups[uname]) groups[uname] = [];
      groups[uname].push(cfg);
    });

    // Sort so current user is always on top
    const ordered = {};
    if (currentUsername && groups[currentUsername]) {
      ordered[currentUsername] = groups[currentUsername];
      delete groups[currentUsername];
    }
    return { ...ordered, ...groups };
  }, [configList, currentUsername]);

  const fetchBranches = async () => {
    try {
      setBranchesLoading(true);
      setBranchesError('');
      const data = await getKivoBranches();
      const branchList = Array.isArray(data?.branches) ? data.branches : [];
      setBranches(branchList);
    } catch (error) {
      console.error('Error fetching Kivo branches:', error);
      const message = error?.message || 'Unable to load branches at the moment';
      setBranchesError(message);
    } finally {
      setBranchesLoading(false);
    }
  };

  const refreshCompanyBranches = async (companyId, companyLabel) => {
    if (!companyId) return;
    setRefreshingCompanies((prev) => ({ ...prev, [companyId]: true }));
    try {
      const token = getStoredToken();
      const response = await fetch(
        buildApiUrl(`/integrations/kivo/branches/refresh?company_id=${encodeURIComponent(companyId)}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to refresh branches');
      }
      const payload = await response.json();
      showToast(
        payload?.message || `Branches refreshed for ${companyLabel}`,
        'success'
      );
      await fetchBranches();
    } catch (error) {
      console.error('Error refreshing Kivo branches:', error);
      showToast(error?.message || 'Failed to refresh branches', 'error');
    } finally {
      setRefreshingCompanies((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const handleTabChange = (_event, newValue) => {
    if (newValue === 1 && !branchesLoading && branches.length === 0) {
      fetchBranches();
    }
    setActiveTab(newValue);
  };

  const branchGroups = useMemo(() => {
    const grouped = {};
    branches.forEach((branch) => {
      const companyId = branch.company_id || '';
      const companyLabel = branch.company_name || branch.company_id || 'Unspecified Company';
      if (!grouped[companyId]) {
        grouped[companyId] = {
          label: companyLabel,
          branches: [],
        };
      }
      grouped[companyId].branches.push(branch);
    });
    return grouped;
  }, [branches]);

  const branchEntries = useMemo(() => Object.entries(branchGroups), [branchGroups]);

  const fetchKivoConfig = async ({ background = false, targetPage = page } = {}) => {
    try {
      if (background) {
        setLoadingMore(true);
      } else {
        setFetchingConfig(true);
      }
      if (targetPage === 1 && !background) {
        setConfigList([]);
      }
      const token = getStoredToken();
      const response = await fetch(
        buildApiUrl(`/integrations/kivo/config?page=${targetPage}&page_size=${pageSize}`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const configs = data?.configs || [];
        const pagination = data?.pagination || {};
        const totalPagesFromServer = Math.max(1, Number(pagination.total_pages) || 1);
        const totalItems =
          typeof pagination.total === 'number' ? pagination.total : configs.length;

        setTotalPages(totalPagesFromServer);
        setConfigList(configs);

        if (!companyBaseUrl && configs.length) {
          const defaultBase = configs.find((cfg) => cfg.base_url && cfg.base_url.trim())?.base_url;
          if (defaultBase) {
            setCompanyBaseUrl(defaultBase);
          }
        }

        setConfigured(totalItems > 0);
        setPage(targetPage);
        await fetchKivoProfiles(configs);
        setAccessToken('');
        setAttendanceToken('');
        setCompanyId('');
        setCompanyName('');
        setMakeDefault(false);
      } else if (response.status === 404) {
        // No configuration found yet
        setConfigured(false);
        setConfigList([]);
        setTotalPages(1);
      }

      await fetchBranches();
    } catch (error) {
      console.error('Error fetching Kivo config:', error);
      setConfigured(false);
      setConfigList([]);
      setTotalPages(1);
    } finally {
      if (background) {
        setLoadingMore(false);
      } else {
        setFetchingConfig(false); // avoid flickering UI during background refreshes
      }
    }
  };

  const fetchProfilesForConfig = async (configId) => {
    if (!configId) return;
    setProfilesLoadingMap((prev) => ({ ...prev, [configId]: true }));
    setProfilesErrorMap((prev) => ({ ...prev, [configId]: '' }));
    try {
      const data = await getKivoProfiles(configId);
      const list = Array.isArray(data?.profiles) ? data.profiles : [];
      setKivoProfilesByConfig((prev) => ({ ...prev, [configId]: list }));
      setSelectedProfileKeysByConfig((prev) => ({
        ...prev,
        [configId]: (prev[configId] || []).filter((key) => list.some((profile) => buildProfileKey(profile) === key)),
      }));
      setSelectedCompaniesByConfig((prev) => ({ ...prev, [configId]: prev[configId] || [] }));
    } catch (error) {
      console.error('Error fetching Kivo profiles:', error);
      let friendlyMessage = error?.message ? String(error.message) : 'Unable to load Kivo profiles';
      const lowerMessage = friendlyMessage.toLowerCase();
      if (lowerMessage.includes('no kivo configuration') || lowerMessage.includes('http error! status: 404')) {
        friendlyMessage = 'Connect a token first to preview Kivo profiles.';
      }
      setProfilesErrorMap((prev) => ({ ...prev, [configId]: friendlyMessage }));
      setKivoProfilesByConfig((prev) => ({ ...prev, [configId]: [] }));
    } finally {
      setProfilesLoadingMap((prev) => ({ ...prev, [configId]: false }));
    }
  };

  const fetchKivoProfiles = async (configs = []) => {
    await Promise.all((configs || []).map((cfg) => fetchProfilesForConfig(cfg.config_id)));
  };

  const handlePageChange = async (_event, value) => {
    if (value === page || fetchingConfig || loadingMore) return;
    await fetchKivoConfig({ background: true, targetPage: value });
  };

  const buildProfileKey = (profile) => `${String(profile.company?.id || 'nocompany')}:${String(profile.id || 'noprofile')}`;
  const existingCompanyIds = useMemo(() => new Set(configList.map((c) => String(c.company_id || ''))), [configList]);

  const getAvailableProfiles = (configId) => {
    const profiles = kivoProfilesByConfig[configId] || [];
    return profiles.filter((profile) => {
      if (profile.is_current) return false;
      const profileCompanyId = profile.company?.id ? String(profile.company.id) : '';
      if (profileCompanyId && existingCompanyIds.has(profileCompanyId)) return false;
      if (!currentCompanyId) {
        return true;
      }
      return !profileCompanyId || profileCompanyId !== currentCompanyId;
    });
  };

  const toggleProfileSelection = (configId, profileKey) => {
    setSelectedProfileKeysByConfig((prev) => {
      const current = prev[configId] || [];
      const next = current.includes(profileKey)
        ? current.filter((key) => key !== profileKey)
        : [...current, profileKey];
      return { ...prev, [configId]: next };
    });
  };

  const handleRemoveSelectedCompany = (configId, companyId, companyName) => {
    setSelectedCompaniesByConfig((prev) => {
      const list = prev[configId] || [];
      return {
        ...prev,
        [configId]: list.filter((c) => !(String(c.companyId || '') === String(companyId || '') && String(c.companyName || '') === String(companyName || ''))),
      };
    });
    setSelectedProfileKeysByConfig((prev) => {
      const current = prev[configId] || [];
      const filtered = current.filter((key) => {
        const [cid] = key.split(':');
        return cid !== String(companyId || '');
      });
      return { ...prev, [configId]: filtered };
    });
  };

  const handleDeleteConfig = async (configId) => {
    try {
      const token = getStoredToken();
      const response = await fetch(buildApiUrl(`/integrations/kivo/config?config_id=${encodeURIComponent(configId)}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete company mapping');
      }
      let nextConfigsSnapshot = [];
      setConfigList((prev) => {
        const nextConfigs = (prev || []).filter((cfg) => cfg.config_id !== configId);
        nextConfigsSnapshot = nextConfigs;
        return nextConfigs;
      });
      setConfigured((nextConfigsSnapshot || []).length > 0);
      setKivoProfilesByConfig((prev) => {
        const { [configId]: _removed, ...rest } = prev || {};
        return rest;
      });
      setProfilesLoadingMap((prev) => {
        const { [configId]: _removed, ...rest } = prev || {};
        return rest;
      });
      setProfilesErrorMap((prev) => {
        const { [configId]: _removed, ...rest } = prev || {};
        return rest;
      });
      setSelectedProfileKeysByConfig((prev) => {
        const { [configId]: _removed, ...rest } = prev || {};
        return rest;
      });
      setSelectedCompaniesByConfig((prev) => {
        const { [configId]: _removed, ...rest } = prev || {};
        return rest;
      });
      showToast('Company mapping removed', 'success');
    } catch (err) {
      console.error('Delete company config error:', err);
      showToast(err.message || 'Failed to delete company mapping', 'error');
    }
  };

  const handleSetDefault = async (cfg) => {
    try {
      const token = getStoredToken();
      const response = await fetch(buildApiUrl('/integrations/kivo/config'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: null, // backend will reuse existing token
          company_id: cfg.company_id || null,
          company_name: cfg.company_name || null,
          is_default: true,
          source_config_id: cfg.config_id || null,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to set default token');
      }
      await fetchKivoConfig({ background: true, targetPage: 1 });
      showToast('Default token updated', 'success');
    } catch (err) {
      console.error('Set default token error:', err);
      showToast(err.message || 'Failed to set default token', 'error');
    }
  };

  const handleAddSelectedCompanies = async (configId) => {
    const availableProfiles = getAvailableProfiles(configId);
    const selectedKeys = selectedProfileKeysByConfig[configId] || [];
    const selectedEntries = availableProfiles
      .filter((profile) => selectedKeys.includes(buildProfileKey(profile)))
      .map((profile) => ({
        companyId: profile.company?.id ? String(profile.company.id) : '',
        companyName: profile.company?.name ? profile.company.name.trim() : '',
      }))
      .filter((entry) => entry.companyId || entry.companyName);

    const uniqueEntries = [];
    const seen = new Set();
    const configBaseUrl = configList.find((cfg) => cfg.config_id === configId)?.base_url;
    const fallbackBaseUrl = configBaseUrl || companyBaseUrl;
    selectedEntries.forEach((entry) => {
      const key = `${entry.companyId}:${entry.companyName}`;
      if (existingCompanyIds.has(entry.companyId)) return; // prevent duplicates already saved
      if (!seen.has(key)) {
        seen.add(key);
        uniqueEntries.push({
          ...entry,
          baseUrl: entry.baseUrl || fallbackBaseUrl,
        });
      }
    });

    if (uniqueEntries.length === 0) {
      showToast('This company is already added', 'success');
      return;
    }

    if (uniqueEntries.length === 0) {
      showToast('This company is already added', 'success');
      return;
    }

    setSelectedCompaniesByConfig((prev) => ({ ...prev, [configId]: uniqueEntries }));

    // Persist immediately so it reaches the DB (backend will reuse existing token if none provided)
    if (uniqueEntries.length > 0) {
      await handleSubmit(null, uniqueEntries, configId);
    }
  };

  const handleSubmit = async (e, companiesOverride = null, sourceConfigId = null) => {
    e?.preventDefault();
    const trimmedToken = accessToken.trim();
    const trimmedAttendanceToken = attendanceToken.trim();

    const hasExistingToken = configList.length > 0;
    if (!trimmedToken && !hasExistingToken) {
      setErrorMessage('Access Token is required');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    // Build payloads for all selected companies; fallback to manual entry if none selected
    const normalizedManualCompanyId = companyId.trim();
    const normalizedManualCompanyName = companyName.trim();
    const normalizedManualBaseUrl = companyBaseUrl.trim();
    const companiesToPersist = Array.isArray(companiesOverride)
      ? companiesOverride
      : (sourceConfigId ? (selectedCompaniesByConfig[sourceConfigId] || []) : []);
    const shouldIncludeManual = !!normalizedManualCompanyId || !!normalizedManualCompanyName || companiesToPersist.length === 0;
    const payloads = [];

    if (shouldIncludeManual && companiesToPersist.length === 0 && !normalizedManualBaseUrl) {
      setErrorMessage('Base URL is required when adding a company manually.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    const unique = new Set();
    companiesToPersist.forEach((entry) => {
      const cid = entry.companyId || '';
      const cname = entry.companyName || '';
      const key = `${cid}:${cname}`;
      if (unique.has(key)) return;
      if (cid && existingCompanyIds.has(cid)) return; // Skip already-added companies
      unique.add(key);
      const entryBaseUrl = (entry.baseUrl || normalizedManualBaseUrl || '').trim();
      const entryPayload = {
        company_id: cid || null,
        company_name: cname || null,
        base_url: entryBaseUrl || null,
        is_default: false,
      };
      if (trimmedAttendanceToken) {
        entryPayload.attendance_access_token = trimmedAttendanceToken;
      }
      payloads.push(entryPayload);
    });

    if (shouldIncludeManual) {
      const manualKey = `${normalizedManualCompanyId}:${normalizedManualCompanyName}`;
      if (!unique.has(manualKey)) {
        const manualEntry = {
          company_id: normalizedManualCompanyId || null,
          company_name: normalizedManualCompanyName || null,
          base_url: normalizedManualBaseUrl || null,
          is_default: makeDefault || configList.length === 0,
        };
        if (trimmedAttendanceToken) {
          manualEntry.attendance_access_token = trimmedAttendanceToken;
        }
        payloads.push(manualEntry);
      }
    }

    // Ensure there's always a default config when none exist yet
    if (payloads.length && (!configList.length || makeDefault)) {
      payloads[0].is_default = true;
    }

    if (!payloads.length) {
      setErrorMessage('Select a company or provide company details to save.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    try {
      setLoading(true);
      setShowError(false);
      setShowSuccess(false);

      const token = getStoredToken();
      const requestBody = {
        access_token: trimmedToken || null, // backend will reuse existing token if null
        companies: payloads,
        // Keep single-company compatibility if only manual is present
        company_id: shouldIncludeManual && payloads.length === 1 ? payloads[0].company_id : null,
        company_name: shouldIncludeManual && payloads.length === 1 ? payloads[0].company_name : null,
        is_default: payloads[0]?.is_default || false,
        source_config_id: sourceConfigId || null,
      };
      if (trimmedAttendanceToken) {
        requestBody.attendance_access_token = trimmedAttendanceToken;
      }

      const response = await fetch(buildApiUrl('/integrations/kivo/config'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMsg = 'Failed to save Kivo configuration';
        if (errorData.detail) {
          errorMsg = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : JSON.stringify(errorData.detail);
        }
        throw new Error(errorMsg);
      }

      const responseData = await response.json();

      const newConfigs = responseData?.configs
        ? responseData.configs
        : responseData?.config
          ? [responseData.config]
          : configList;
      setConfigList(newConfigs || []);
      setConfigured((newConfigs || []).length > 0);
      const message = responseData?.message || 'Kivo token saved successfully!';
      
      setSuccessMessage(message);
      setShowSuccess(true);
      await fetchKivoConfig({ background: true, targetPage: 1 });
      await fetchKivoProfiles();
      setAccessToken('');
      setCompanyId('');
      setCompanyName('');
      setCompanyBaseUrl('');
      setMakeDefault(false);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving Kivo config:', error);
      setErrorMessage(error.message || 'An error occurred while saving the configuration');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingConfig) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 3, sm: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}
    >
      <PageHeader 
        title="Connect with Kivo" 
        subtitle="Configure your Kivo integration settings"
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The token you used to log in is stored automatically as the default configuration. Use the form below to add company-specific tokens by providing a company name (and optional ID) plus its access token.
      </Typography>

      {showSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircleIcon />}
          onClose={() => setShowSuccess(false)}
        >
          {successMessage}
        </Alert>
      )}

      {showError && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<ErrorIcon />}
          onClose={() => setShowError(false)}
        >
          {errorMessage}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        allowScrollButtonsMobile
        sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Configurations" />
        <Tab label="Branches" />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {configured && configList.length > 0 && (
          <>
            {Object.entries(groupedConfigs).map(([username, userConfigs]) => (
              <Card
                key={username}
                sx={{
                  mb: 4,
                  borderLeft: '4px solid #40518A',
                  backgroundColor: '#ffffff'
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">
                      {username} {username === currentUsername && '(You)'}
                    </Typography>
                    <Chip
                      label={`${userConfigs.length} token${userConfigs.length > 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  {userConfigs.map((cfg) => {
                    const availableProfiles = getAvailableProfiles(cfg.config_id);
                    const profilesLoading = profilesLoadingMap[cfg.config_id];
                    const profilesError = profilesErrorMap[cfg.config_id];
                    const selectedProfileKeys = selectedProfileKeysByConfig[cfg.config_id] || [];
                    const selectedCompanies = selectedCompaniesByConfig[cfg.config_id] || [];
                    const addedCompanyLabel = cfg.company_name || 'Default Company';
                    const addedCompanyId = cfg.company_id;

                    return (
                      <Box
                        key={cfg.config_id}
                        sx={{
                          mb: 3,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          background: '#fff'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ minWidth: 0 }}>
                              {cfg.is_default ? 'Login Token' : 'Custom Token'}
                              {cfg.company_name ? ` — ${cfg.company_name}` : ''}
                            </Typography>
                            {cfg.is_default ? <Chip size="small" label="default" color="success" /> : null}
                          </Box>

                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                          >
                            {!cfg.is_default ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSetDefault(cfg)}
                                fullWidth={isMobile}
                              >
                                Set as default
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => handleDeleteConfig(cfg.config_id)}
                              fullWidth={isMobile}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>

                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          Token: {cfg.access_token || 'N/A'}
                          {cfg.company_id ? ` | Company ID: ${cfg.company_id}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          Attendance Token: {cfg.attendance_access_token || 'Not set'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, wordBreak: 'break-word' }}>
                          Base URL: {cfg.base_url || 'Environment default'}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          <Chip
                            label={`${addedCompanyLabel}${addedCompanyId ? ` (${addedCompanyId})` : ''}`}
                            size="small"
                          />
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        <Typography variant="subtitle2" gutterBottom>
                          Available companies for this token
                        </Typography>

                        {profilesLoading ? (
                          <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : profilesError ? (
                          <Alert severity="info">{profilesError}</Alert>
                        ) : availableProfiles.length ? (
                          <>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {availableProfiles.map((profile) => {
                                const profileKey = buildProfileKey(profile);
                                const isSelected = selectedProfileKeys.includes(profileKey);
                                return (
                                  <Grid item xs={12} md={6} key={`profile-${profileKey}`}>
                                    <Box
                                      sx={{
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        background: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                      }}
                                    >
                                      <Checkbox
                                        checked={isSelected}
                                        onChange={() =>
                                          toggleProfileSelection(cfg.config_id, profileKey)
                                        }
                                      />
                                      <Box flex="1">
                                        <Typography variant="subtitle2">
                                          {profile.company?.name || 'Unknown company'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Profile ID: {profile.id || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                            
                            {/* Add button to submit selected companies */}
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleAddSelectedCompanies(cfg.config_id)}
                                disabled={selectedProfileKeys.length === 0 || loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                                fullWidth={isMobile}
                              >
                                Add Selected {selectedProfileKeys.length > 0 ? `(${selectedProfileKeys.length})` : 'Companies'}
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No additional companies available.
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {configured && totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mt: 2,
              mb: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              disabled={fetchingConfig || loadingMore}
              size={isMobile ? 'small' : 'medium'}
            />
            {loadingMore && <CircularProgress size={24} />}
          </Box>
        )}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {configured ? 'Add another company token' : 'Add your first company token'}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    type="text"
                    variant="outlined"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    autoComplete="off"
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Company ID"
                    type="text"
                    variant="outlined"
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    placeholder="Internal identifier (optional)"
                    helperText="Optional identifier to scope this token to a company"
                    autoComplete="off"
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Base URL"
                    type="url"
                    variant="outlined"
                    value={companyBaseUrl}
                    onChange={(e) => setCompanyBaseUrl(e.target.value)}
                    placeholder="https://www.kivo.ai"
                    helperText="Base URL for Kivo APIs (e.g., https://www.kivo.ai)"
                    autoComplete="off"
                    disabled={loading}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    required={!configList.length}
                    label="Access Token"
                    type="text"
                    variant="outlined"
                    InputLabelProps={{
                      sx: {
                        transform: 'translate(14px, 12px) scale(1)',
                        WebkitTransform: 'translate(14px, 10px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          WebkitTransform: 'translate(14px, -9px) scale(0.75)'
                        }
                      }
                    }}
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    helperText={
                      configList.length
                        ? "Optional. Leave blank to reuse the stored token, or provide a new token to override."
                        : "This token will become the default configuration"
                    }
                    autoComplete="off"
                    disabled={loading}
                  />
                  <TextField
                    fullWidth
                    label="Attendance Token"
                    type="text"
                    variant="outlined"
                    value={attendanceToken}
                    onChange={(e) => setAttendanceToken(e.target.value)}
                    helperText="Optional token used only for attendance syncs (falls back to access token when blank)"
                    autoComplete="off"
                    disabled={loading}
                    sx={{ mt: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={makeDefault || (!configList.length)}
                          onChange={(e) => setMakeDefault(e.target.checked)}
                          disabled={loading}
                        />
                      }
                      label="Set as default token"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Sync />}
                      disabled={loading || (!accessToken.trim() && !configList.length)}
                      size="large"
                      fullWidth={isMobile}
                      sx={{
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        px: { xs: 2, sm: 4 },
                      }}
                    >
                      {loading ? 'Saving...' : 'Add Token'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="h6">Branches</Typography>
              <Chip
                label={`${branches.length} branch${branches.length === 1 ? '' : 'es'}`}
                size="small"
                color="primary"
              />
            </Box>
            <Divider sx={{ mb: 2 }} />

            {branchesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="180px">
                <CircularProgress />
              </Box>
            ) : branchesError ? (
              <Alert severity="error">{branchesError}</Alert>
            ) : branchEntries.length ? (
              branchEntries.map(([companyId, group], idx) => {
                const companyBranches = group.branches;
                const companyLabel = group.label;
                const refreshing = !!refreshingCompanies[companyId];
                const companyColor = companyCardColors[idx % companyCardColors.length];
                return (
                  <Box
                    key={`${companyId}-${idx}`}
                    sx={{
                      mb: idx < branchEntries.length - 1 ? 3 : 0,
                      background: '#FBFCFF',
                      borderRadius: 2,
                      p: 2,
                      border: `1px solid ${companyColor}`,
                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {companyLabel}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={`${companyBranches.length} ${companyBranches.length === 1 ? 'branch' : 'branches'}`}
                          size="small"
                          variant="outlined"
                        />
                        {companyId ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => refreshCompanyBranches(companyId, companyLabel)}
                            disabled={refreshing}
                            startIcon={refreshing ? <CircularProgress size={16} /> : null}
                            fullWidth={isMobile}
                          >
                            Refresh
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      {companyBranches.map((branch, branchIdx) => {
                        const branchName = branch.branch_name && branch.branch_name.trim();
                        const displayName = branchName || `Branch ${branchIdx + 1}`;
                        return (
                          <Grid item xs={12} md={6} key={branch.branch_id || `${companyId}-${branchIdx}`}>
                            <Box
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                p: 2,
                                background: '#fff',
                                height: '100%',
                              }}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2">{displayName}</Typography>
                                <Chip
                                  label={`Devices: ${branch.device_count ?? 0}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {branch.branch_address || 'Address not provided.'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {branch.branch_phone_number || 'Phone not provided.'}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                No branches found yet. Add a company to fetch its branches automatically.
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

const TabPanel = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    sx={{ width: '100%' }}
  >
    {value === index && (
      <Box sx={{ mt: 2 }}>
        {children}
      </Box>
    )}
  </Box>
);

export default ConnectWithKivo;
